import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImagePlus, Trash2, Loader2, Clock } from 'lucide-react';
import api from '../utils/api';

const getImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${url}`;
};

export default function StoriesManager() {
  const { id } = useParams();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const loadStories = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/gallery/stories`);
      setStories(response.data.stories || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStories();
  }, [id]);

  const publishStory = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('title', title);
      formData.append('description', description);
      await api.post(`/barbershops/${id}/gallery/admin/stories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTitle('');
      setDescription('');
      await loadStories();
    } finally {
      setSaving(false);
    }
  };

  const deleteStory = async (storyId) => {
    if (!confirm('Excluir esta publicação?')) return;
    await api.delete(`/barbershops/${id}/gallery/admin/stories/${storyId}`);
    setStories(stories.filter(story => story.id !== storyId));
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <ImagePlus className="w-5 h-5 text-primary-400" />
          Publicações
        </h2>

        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder="Título" />
          <label className="btn-primary cursor-pointer justify-center">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5 mr-2" />}
            Publicar
            <input type="file" accept="image/*" className="hidden" onChange={(e) => publishStory(e.target.files?.[0])} />
          </label>
        </div>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input resize-none mt-3" rows={2} placeholder="Legenda" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stories.map((story) => (
          <div key={story.id} className="card overflow-hidden border border-dark-700">
            <img src={getImageUrl(story.image_url)} alt={story.title || 'Publicação'} className="w-full aspect-[9/12] object-cover" />
            <div className="p-4 space-y-2">
              <p className="font-semibold text-white">{story.title || 'Publicação'}</p>
              {story.description && <p className="text-sm text-gray-400">{story.description}</p>}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  expira em 24h
                </span>
                <button onClick={() => deleteStory(story.id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!stories.length && (
        <div className="card p-10 text-center text-gray-400">Nenhuma publicação ativa.</div>
      )}
    </div>
  );
}
